import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { ShieldAlert, Skull, KeyRound, Lock } from "lucide-react";

const statusColor = { active: "#39FF14", standby: "#FFD700", isolated: "#FF003C" };

export default function Security() {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["governance"],
        queryFn: async () => (await api.get("/governance")).data,
    });
    const s = data?.settings || {};
    const [caps, setCaps] = useState({ minimax_cap: "", youtube_cap: "", upload_daily_target: "" });
    const [restDay, setRestDay] = useState("");

    useEffect(() => {
        if (data?.settings) {
            setCaps({
                minimax_cap: s.minimax_cap,
                youtube_cap: s.youtube_cap,
                upload_daily_target: s.upload_daily_target ?? 10,
            });
            setRestDay((s.rest_days && s.rest_days.length) ? String(s.rest_days[0]) : "");
        }
        // eslint-disable-next-line
    }, [data]);

    const update = useMutation({
        mutationFn: async (payload) => (await api.put("/governance", payload)).data,
        onMutate: async (payload) => {
            await qc.cancelQueries({ queryKey: ["governance"] });
            const prev = qc.getQueryData(["governance"]);
            qc.setQueryData(["governance"], (old) =>
                old ? { ...old, settings: { ...old.settings, ...payload } } : old,
            );
            return { prev };
        },
        onSuccess: () => toast.success("Gobernanza actualizada"),
        onError: (_e, _v, ctx) => {
            if (ctx?.prev) qc.setQueryData(["governance"], ctx.prev);
            toast.error("Fallo actualizando");
        },
        onSettled: () => qc.invalidateQueries({ queryKey: ["governance"] }),
    });

    return (
        <div data-testid="security-page">
            <PageHead
                title="SEGURIDAD Y GOBERNANZA"
                sub="POLP · agente Destroyer aislado · x-api-key en mutaciones · vault cifrado en el VPS · cuotas de gasto y disclosure de IA."
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <Panel title="Topes de gasto" testId="quota-form" className="lg:col-span-1">
                    {[
                        ["minimax_cap", "Minimax ($ estimado)"],
                        ["youtube_cap", "YouTube API (unidades)"],
                        ["upload_daily_target", "Subidas/día a YouTube (marcapasos)"],
                    ].map(([k, label]) => (
                        <div key={k} className="mb-3">
                            <div className="label mb-1">{label}</div>
                            <input
                                data-testid={`cap-${k}`}
                                type="number"
                                min={k === "upload_daily_target" ? 1 : 0}
                                value={caps[k] ?? ""}
                                onChange={(e) => setCaps({ ...caps, [k]: e.target.value })}
                                className="w-full border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                            />
                        </div>
                    ))}
                    <div className="mb-3">
                        <div className="label mb-1">Día de descanso semanal (sin publicaciones)</div>
                        <select
                            data-testid="rest-day-select"
                            value={restDay}
                            onChange={(e) => setRestDay(e.target.value)}
                            className="w-full border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                        >
                            <option value="">Ninguno</option>
                            {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"].map((d, i) => (
                                <option key={i} value={i}>{d}</option>
                            ))}
                        </select>
                    </div>
                    <Btn
                        testId="save-quotas-btn"
                        className="mt-2 w-full"
                        onClick={() =>
                            update.mutate({
                                minimax_cap: Number(caps.minimax_cap),
                                youtube_cap: Number(caps.youtube_cap),
                                upload_daily_target: Number(caps.upload_daily_target),
                                rest_days: restDay === "" ? [] : [Number(restDay)],
                            })
                        }
                    >
                        Guardar topes
                    </Btn>
                </Panel>

                <Panel title="Controles de acceso" testId="access-controls" className="lg:col-span-1">
                    <div className="flex items-center justify-between border border-border p-3">
                        <div>
                            <div className="flex items-center gap-2 text-sm">
                                <KeyRound size={14} className="text-primary" /> x-api-key en mutaciones
                            </div>
                            <div className="mt-1 text-[10px] text-muted-foreground">
                                Exige la clave en endpoints POST/PUT/DELETE
                            </div>
                        </div>
                        <button
                            data-testid="toggle-apikey"
                            onClick={() => update.mutate({ enforce_api_key: !s.enforce_api_key })}
                            className="border px-3 py-1 text-xs uppercase"
                            style={{
                                color: s.enforce_api_key ? "#39FF14" : "#4A4A4A",
                                borderColor: s.enforce_api_key ? "#39FF14" : "#4A4A4A",
                            }}
                        >
                            {s.enforce_api_key ? "ON" : "OFF"}
                        </button>
                    </div>
                    <ul className="mt-4 space-y-2 text-xs text-muted-foreground">
                        <li className="flex items-center gap-2">
                            <Lock size={12} className="text-primary" /> Vault cifrado (Fernet/AES) — credenciales de integraciones y tokens OAuth cifrados en reposo
                        </li>
                        <li className="flex items-center gap-2">
                            <ShieldAlert size={12} className="text-primary" /> Preguntar → Confirmar → Ejecutar
                        </li>
                        <li className="flex items-center gap-2">
                            <StatusDot color="#39FF14" /> Subida en private por defecto
                        </li>
                        <li className="flex items-center gap-2">
                            <StatusDot color="#39FF14" /> Disclosure de IA + anti-repetición
                        </li>
                    </ul>
                </Panel>

                <Panel title="Agentes (POLP)" testId="agents-panel" className="lg:col-span-1">
                    <ul className="space-y-3">
                        {(data?.agents || []).map((a) => (
                            <li key={a.id} className="border border-border p-3" data-testid={`agent-${a.name}`}>
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2 text-sm">
                                        {a.destructive && <Skull size={13} className="text-destructive" />}
                                        {a.name}
                                    </span>
                                    <span className="flex items-center gap-1.5 text-[10px] uppercase">
                                        <StatusDot color={statusColor[a.status]} pulse={a.status === "active"} />
                                        {a.status}
                                    </span>
                                </div>
                                <div className="mt-1 text-[10px] text-muted-foreground">
                                    {a.role} · {a.brain}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {a.scope.map((sc, i) => (
                                        <span key={i} className="border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground">
                                            {sc}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>

            <Panel title="Log de auditoría" testId="audit-log" className="mt-6">
                <ul className="space-y-1.5 font-mono text-xs">
                    {(data?.audit_logs || []).map((l) => (
                        <li key={l.id} className="flex gap-3">
                            <span
                                style={{
                                    color:
                                        l.level === "warning"
                                            ? "#FFD700"
                                            : l.level === "error"
                                            ? "#FF003C"
                                            : "#00FFFF",
                                }}
                            >
                                [{l.action}]
                            </span>
                            <span className="text-primary">{l.actor}</span>
                            <span className="text-muted-foreground">{l.detail}</span>
                        </li>
                    ))}
                </ul>
            </Panel>
        </div>
    );
}
